import { useState, useEffect } from "react";
import apiClient from "../services/api";
import { Bell, X, CheckCircle2, ClipboardList } from "lucide-react";
import { Button } from "./ui/button";

const todayKey = () => new Date().toISOString().split("T")[0];
const DISMISS_KEY = "v2r_foreman_digest_dismissed";

/**
 * Push-style daily digest banner for the foreman's Daily Entry page.
 * Surfaces unread in-app notifications pushed to foremen (task/due-date alerts,
 * announcements) — the in-app replacement for the old SMS alerts.
 * Missing daily reports are handled separately by DailyEntryAlert, so they are
 * excluded here to avoid duplication.
 */
const ForemanDigestBanner = () => {
  const [notifications, setNotifications] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [hidden, setHidden] = useState(
    () => localStorage.getItem(DISMISS_KEY) === todayKey()
  );

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await apiClient.get(`/notifications/app-notifications`);
        if (!active) return;
        const items = (data?.notifications || []).filter(
          (n) => !n.is_read && n.type !== "missing_reports"
        );
        setNotifications(items);
      } catch (e) {
        // Non-blocking: digest is best-effort
      } finally {
        if (active) setLoaded(true);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const hideForToday = () => {
    localStorage.setItem(DISMISS_KEY, todayKey());
    setHidden(true);
  };

  const handleGotIt = async () => {
    const ids = notifications.map((n) => n.id);
    hideForToday();
    // Mark each as read so the bell count also clears
    await Promise.allSettled(
      ids.map((id) =>
        apiClient.post(`/notifications/app-notifications/${id}/read`)
      )
    );
  };

  if (!loaded || hidden || notifications.length === 0) return null;

  return (
    <div
      className="mb-4 overflow-hidden rounded-xl border border-amber-300/70 bg-gradient-to-br from-amber-50 to-white shadow-sm animate-fade-in"
      data-testid="foreman-digest-banner"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-amber-500 text-white shadow">
          <Bell className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-amber-900">
              Today's Digest
              <span className="ml-2 rounded-full bg-amber-500 px-2 py-0.5 text-xs font-medium text-white">
                {notifications.length}
              </span>
            </h3>
            <button
              type="button"
              onClick={hideForToday}
              className="text-amber-700/70 transition-colors hover:text-amber-900"
              data-testid="digest-close-btn"
              aria-label="Hide for today"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-2 space-y-2">
            {notifications.map((n) => (
              <div
                key={n.id}
                className="flex items-start gap-2"
                data-testid={`digest-item-${n.id}`}
              >
                <ClipboardList className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                <p className="text-sm text-amber-900">
                  <span className="font-medium">{n.title}</span>
                  {n.message ? ` — ${n.message}` : ""}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={handleGotIt}
              className="h-8 border-amber-400 text-amber-800 hover:bg-amber-100"
              data-testid="digest-got-it-btn"
            >
              <CheckCircle2 className="mr-1.5 h-4 w-4" /> Got it
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForemanDigestBanner;
